// ============================================================
// claimLive.ts — the Claim card's LIVE updates (B2 polish, audit L11).
// Someone else saves the Claim group → the server's "case updated" event
// carries it (summary.claim — caseController.updateCase). This viewer's open
// case takes it at once (applyRemoteCaseEdit: no re-save, baselines advanced)
// — UNLESS they have the Claim card open as a draft: a draft is never
// overwritten. Then the change waits here (pending) and the card says
// "Updated by [name]. Reload"; Reload (or Cancel) takes it.
// ============================================================
import { create } from 'zustand'
import { useCasevoStore } from '../store/useCasevoStore'
import { applyRemoteCaseEdit } from './useAutoSave'
import { claimOfRaw } from '../utils/caseClaim'
import type { CaseClaim } from '../types/case.types'

export interface PendingClaim { caseId: string; claim: CaseClaim; by: string; at: string }
interface ClaimLiveState {
  // The open case's Claim card is being edited (a draft exists).
  draftOpen: boolean
  pending: PendingClaim | null
  setDraftOpen: (open: boolean) => void
  setPending: (p: PendingClaim | null) => void
}
export const useClaimLive = create<ClaimLiveState>((set) => ({
  draftOpen: false,
  pending: null,
  setDraftOpen: (draftOpen) => set({ draftOpen }),
  setPending: (pending) => set({ pending }),
}))

// Put a claim into the open case without re-saving it. "Set by" follows a
// changed own date (the person who changed it).
export const applyClaimNow = (p: PendingClaim): void => {
  const cc = useCasevoStore.getState().currentCase
  if (!cc || cc.id !== p.caseId) return
  const dateChanged = p.claim.limitationOverrideDate !== (cc.claim?.limitationOverrideDate ?? null)
  const setBy = !p.claim.limitationOverrideDate ? null : dateChanged ? { name: p.by, at: p.at } : cc.limitationOverrideSetBy ?? null
  // (B2b) "No time limit" confirmed / removed by them → who + when follows.
  const was = cc.claim?.abuseNoLimitConfirmedAt ?? null
  const now = p.claim.abuseNoLimitConfirmedAt ?? null
  const confirmedBy = !now ? null : now !== was ? { name: p.by, at: now } : cc.abuseNoLimitConfirmedBy ?? null
  applyRemoteCaseEdit({ claim: p.claim, limitationOverrideSetBy: setBy, abuseNoLimitConfirmedBy: confirmedBy }, p.at)
}

// From the live channel: apply now, or hold it while a draft is open.
export const receiveRemoteClaim = (caseId: string, rawClaim: Record<string, unknown>, actorUserId: string, at: string): void => {
  const store = useCasevoStore.getState()
  if (store.currentCase?.id !== caseId) return
  const u = store.firmUsers.find((x) => x.id === actorUserId)
  const by = (u ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() : '') || 'someone else'
  const p: PendingClaim = { caseId, claim: claimOfRaw(rawClaim), by, at }
  if (useClaimLive.getState().draftOpen) { useClaimLive.getState().setPending(p); return }
  applyClaimNow(p)
}
